import { Icon, type IconName } from '../../../components/icons/Icon'
import { StatusPill, type StatusPillVariant } from '../../../components/StatusPill'

export type FilingStatusStepState = 'complete' | 'current' | 'pending'

export interface FilingStatusStep {
  key: string
  label: string
  state: FilingStatusStepState
  /** `null` for a step not yet reached — never a fabricated timestamp. */
  occurredAt: string | null
  actorLabel: string | null
  /** `TenantContext.Channel` rendered verbatim (ui/api/bulk/ai_accept/…) — the audited "how". */
  channel: string | null
}

export interface FilingStatusTimelineProps {
  steps: FilingStatusStep[]
  /** Count of additional, non-milestone events on this filing (e.g. amendment revisions) not
   * shown as their own stage here — surfaced as a single "+N more" affordance, never dropped. */
  additionalEventCount?: number
  /** Always a real action — the sibling "Timeline" tab on this same Case Detail shell
   * (CLAUDE.md §4 "every CTA opens a real page or a defined action"; ≤1 click, provenance budget). */
  onViewFullTimeline: () => void
}

const STATE_ICON: Record<FilingStatusStepState, IconName> = {
  complete: 'check-circle',
  current: 'clock',
  pending: 'minus-circle',
}

const STATE_VARIANT: Record<FilingStatusStepState, StatusPillVariant> = {
  complete: 'success',
  current: 'info',
  pending: 'muted',
}

const STATE_LABEL: Record<FilingStatusStepState, string> = {
  complete: 'Complete',
  current: 'In progress',
  pending: 'Not yet reached',
}

function relativeOrDash(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

/**
 * New per-case component for IB-282 View 2 (docs/design/IB-282-view2-per-case-design.md §5,
 * DoR AC2.4) — a horizontal stepper over the REAL, hash-chained `GET /api/v1/events?target=
 * ProgramFiling,{id}` feed (ADR-0046), not a client-side heuristic. Deliberately distinct from
 * `screens/cases/LifecycleTracker.tsx` (which derives a film-only, cross-source progress estimate
 * for the Overview tab) — this component's steps are 1:1 with real audited `program-filing.*`
 * events (`case_.api.CaseEventTypes`: materialized → certified → amended → outcome-recorded →
 * archived/closed), so every "complete" step carries a real who/when/how, never an inference.
 *
 * Visually mirrors `LifecycleTracker`'s horizontal-stepper pattern (same token set, same
 * `role="list"` a11y shape) so the two "case progress" surfaces on this screen read as one design
 * language, not two competing ones. Presentational only — no fetch; a data-wiring pass maps the
 * Timeline API's `TimelineEntry[]` onto `FilingStatusStep[]` by matching `eventType` against the
 * known milestone set, folding anything else into `additionalEventCount`.
 */
export function FilingStatusTimeline({ steps, additionalEventCount = 0, onViewFullTimeline }: FilingStatusTimelineProps) {
  if (steps.length === 0) {
    return <p style={{ color: 'var(--iw-text-muted)' }}>No filing-status events recorded yet.</p>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      <nav aria-label="Filing status">
        <ol
          role="list"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'stretch',
            gap: 'var(--iw-space-3)',
          }}
        >
          {steps.map((step, index) => (
            <li
              key={step.key}
              aria-current={step.state === 'current' ? 'step' : undefined}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--iw-space-2)', flex: '1 1 190px', minWidth: 190 }}
            >
              {index > 0 ? (
                <span
                  aria-hidden="true"
                  style={{
                    alignSelf: 'center',
                    width: 'var(--iw-space-4)',
                    height: 2,
                    flexShrink: 0,
                    background: steps[index - 1]?.state === 'complete' ? 'var(--iw-success)' : 'var(--iw-border-strong)',
                  }}
                />
              ) : null}
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  flexShrink: 0,
                  border: `2px solid var(--iw-border-strong)`,
                  color: 'var(--iw-text-muted)',
                }}
              >
                <Icon name={STATE_ICON[step.state]} size={14} />
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontWeight: 'var(--iw-weight-medium)' as never, fontSize: 'var(--iw-size-sm)' }}>{step.label}</span>
                <span>
                  <StatusPill variant={STATE_VARIANT[step.state]} label={STATE_LABEL[step.state]} />
                </span>
                {step.occurredAt ? (
                  <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
                    {relativeOrDash(step.occurredAt)}
                    {step.actorLabel ? ` · ${step.actorLabel}` : ''}
                    {step.channel ? ` · ${step.channel}` : ''}
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </nav>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)' }}>
        {additionalEventCount > 0 ? (
          <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
            +{additionalEventCount} other event{additionalEventCount === 1 ? '' : 's'} on this filing
          </span>
        ) : null}
        <button type="button" className="iw-btn iw-btn--sm iw-btn--ghost" onClick={onViewFullTimeline}>
          View full timeline
          <Icon name="chevron-right" size={14} />
        </button>
      </div>
    </div>
  )
}
